import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type { NovoCredito, NovoPreco, PainelDoCredito } from '../../types/creditoDaIa'

/**
 * As chaves do crédito de IA.
 *
 * Invalidação grossa de propósito: declarar um preço muda o saldo, o consumo por preço e a linha do
 * tempo — três blocos da mesma tela. Cache fino aqui economizaria uma requisição e custaria um
 * painel mostrando o saldo antigo ao lado do preço novo.
 */
const chaves = {
  tudo: () => ['admin', 'credito-da-ia'] as const,
  painel: (competencia?: string) => ['admin', 'credito-da-ia', competencia ?? 'corrente'] as const,
}

/**
 * O painel do crédito: saldo, consumo por preço, preços e histórico.
 *
 * @param competencia mês `AAAA-MM`; ausente usa o mês corrente
 * @param habilitada false não consulta — a tarja de `/admin/*` usa isto para não pedir o saldo a
 *     quem o servidor recusaria de qualquer forma
 * @returns a consulta
 */
export function usePainelDoCredito(competencia?: string, habilitada = true) {
  return useQuery({
    queryKey: chaves.painel(competencia),
    enabled: habilitada,
    queryFn: async () =>
      (
        await api.get<PainelDoCredito>('/admin/credito-da-ia', {
          params: competencia ? { competencia } : undefined,
        })
      ).data,
  })
}

/**
 * Declara o crédito do mês.
 *
 * @returns a mutação
 */
export function useDeclararCredito() {
  return useMutacaoDoCredito((corpo: NovoCredito) => api.post('/admin/credito-da-ia', corpo))
}

/**
 * Revoga um crédito. Ele continua na tabela, com quem revogou, quando e por quê.
 *
 * @returns a mutação
 */
export function useRevogarCredito() {
  return useMutacaoDoCredito((corpo: { id: number; motivo: string }) =>
    api.post(`/admin/credito-da-ia/${corpo.id}/revogacao`, { motivo: corpo.motivo }),
  )
}

/**
 * Registra um preço novo. Ele NÃO reprecifica o que já passou.
 *
 * @returns a mutação
 */
export function useRegistrarPreco() {
  return useMutacaoDoCredito((corpo: NovoPreco) => api.post('/admin/credito-da-ia/precos', corpo))
}

/**
 * Encerra a vigência de um preço — a única alteração que ele aceita.
 *
 * @returns a mutação
 */
export function useEncerrarVigencia() {
  return useMutacaoDoCredito((corpo: { id: number }) =>
    api.post(`/admin/credito-da-ia/precos/${corpo.id}/encerramento`, {}),
  )
}

/** Toda mutação do orçamento invalida o painel inteiro. Ver o comentário das chaves. */
function useMutacaoDoCredito<T>(executar: (corpo: T) => Promise<unknown>) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: executar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
      // A pendência do painel do administrador deriva do saldo: sem isto, "Crédito de IA esgotado"
      // continuaria na caixa de entrada depois de o crédito novo ser declarado.
      void cliente.invalidateQueries({ queryKey: ['painel'] })
    },
  })
}
